import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FaPlay, FaTimes, FaHome, FaVideo } from "react-icons/fa";
import { Link } from "react-router-dom";
import "./VideoGallery.css";
import img from "../../assets/breadcrumb/Video-gallery.jpg"; // Using the same breadcrumb image as News page


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


// Helper to extract ID (No changes)
const getYoutubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// Helper to get thumbnail (No changes)
const getThumbnail = (url) => {
  const id = getYoutubeId(url);
  return id
    ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
    : "https://via.placeholder.com/800x450?text=Video+Unavailable";
};

function VideoGallery() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoData, setVideoData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch only public/active videos from backend (No changes)
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/ytVideos/public`,
        );
        const data = await response.json();
        setVideoData(data);
        setLoading(false);
      } catch (error) {
        console.error("Failed to load videos", error);
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  // Modal open/close logic (No changes)
  const handleOpenVideo = (video) => setSelectedVideo(video);
  const handleCloseVideo = () => setSelectedVideo(null);

  // Prevent background scroll when modal is open (No changes)
  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [selectedVideo]);

  const selectedVideoId = selectedVideo
    ? getYoutubeId(selectedVideo.link)
    : null;

  return (
    <>
      <Helmet>
        <title>
          Video Gallery | Sucheta Kriplani Teacher Training College (SKTTC),
          Jodhpur
        </title>

        <meta
          name="description"
          content="Explore campus events, academic activities, cultural programs, PTET admission updates, and student achievements at Sucheta Kriplani Teacher Training College (SKTTC), Manaklao, Jodhpur."
        />

        <meta
          name="keywords"
          content="SKTTC Video Gallery, Sucheta Kriplani College Videos, B.Ed College Jodhpur Events, PTET Activities, Campus Life Manaklao, JNVU Affiliated College"
        />

        <link
          rel="canonical"
          href="https://suchetabedcollege.org/video-gallery"
        />
      </Helmet>

      <div className="video-gallery-wrapper">
        {/* 1. HERO SECTION - The new design you liked */}
        <div className="video-gallery-hero">
          <img
            src={img}
            alt="SK College Campus Events"
            className="video-gallery-hero-img"
          />
          <div className="video-gallery-hero-overlay">
            <h1 className="video-gallery-main-title">Video Gallery</h1>
            <p className="video-gallery-subtitle">
              Experience the vibrant life, achievements, and events through our
              lens.
            </p>
            <div className="video-gallery-breadcrumb">
              <Link to="/" className="video-gallery-bread-item">
                <FaHome /> Home
              </Link>
              <span className="video-gallery-sep">/</span>
              <span className="video-gallery-bread-active">Video Gallery</span>
            </div>
          </div>
        </div>

        <div className="video-gallery-content">
          {/* 2. INTRO TEXT - From the previous design */}
          <div className="video-gallery-intro">
            <h2 className="video-gallery-section-title">
              Our Visual Chronicle
            </h2>
            <p className="video-gallery-section-subtitle">
              Experience the vibrant life, achievements, and memorable events of
              SK TT College through our lens.
            </p>
          </div>

          {/* 3. VIDEO GRID - The previous design you liked */}
          <div className="video-gallery-grid">
            {loading ? (
              <p className="video-gallery-status-text">Loading Videos...</p>
            ) : videoData.length === 0 ? (
              <p className="video-gallery-status-text">
                No videos are currently available. Please check back later.
              </p>
            ) : (
              videoData.map((video) => (
                <div
                  key={video._id}
                  className="video-gallery-card"
                  onClick={() => handleOpenVideo(video)}
                >
                  <div className="video-gallery-thumb-wrapper">
                    <img src={getThumbnail(video.link)} alt={video.title} />
                    <div className="video-gallery-play-overlay">
                      <div className="video-gallery-play-btn">
                        <FaPlay />
                      </div>
                    </div>
                  </div>
                  <div className="video-gallery-card-info">
                    <h3 className="video-gallery-card-title">
                      <FaVideo className="video-gallery-icon" /> {video.title}
                    </h3>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 4. LIGHTBOX MODAL - The previous design you liked */}
        {selectedVideo && selectedVideoId && (
          <div className="video-gallery-lightbox" onClick={handleCloseVideo}>
            <div
              className="video-gallery-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="video-gallery-lightbox-video-container">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideoId}?autoplay=1&rel=0`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <button
                  className="video-gallery-close-btn"
                  onClick={handleCloseVideo}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="video-gallery-lightbox-desc">
                <p>{selectedVideo.title}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VideoGallery;
